import React, { useEffect } from "react";
import { useNotification } from "../contexts/notificationContext";

interface GlobalErrorHandlerProps {
  children: React.ReactNode;
}

const GlobalErrorHandler: React.FC<GlobalErrorHandlerProps> = ({
  children,
}) => {
  const { setNotify } = useNotification();

  useEffect(() => {
    const handleRuntimeError = (error: any, context: string) => {
      console.error(`[${context}] Runtime error detected:`, error);

      const message =
        error?.message || error?.toString() || "Unknown error occurred";

      // Check if this is a wallet-related error
      const isWalletError =
        message.includes("could not coalesce error") ||
        message.includes("missing trie node") ||
        message.includes("Internal JSON-RPC error") ||
        message.includes("Failed to connect") ||
        message.includes("UNKNOWN_ERROR") ||
        message.includes("network error") ||
        message.includes("connection failed") ||
        message.includes("connector.disconnect");

      // Show error notification with appropriate message
      setNotify({
        status: "error",
        txHash: `undefined`,
        errorReason: isWalletError
          ? "Wallet connection error. Wallet disconnected. Wallet disconnected. Please reconnect your wallet"
          : `Runtime Error: ${message}`,
        duration: isWalletError ? 10000 : 8000,
        chainType: "BNB",
      });
    };

    // Handle unhandled promise rejections
    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      event.preventDefault(); // Prevent default browser error handling
      handleRuntimeError(event.reason, "Promise Rejection");
    };

    // Handle global JavaScript errors
    const onGlobalError = (event: ErrorEvent) => {
      event.preventDefault(); // Prevent default browser error handling
      handleRuntimeError(event.error || event.message, "Global Error");
    };

    // Listen for custom error events (for manual error reporting)
    const onCustomError = (event: CustomEvent) => {
      const detail = event.detail;
      const message =
        detail?.message || detail?.toString() || "Unknown error occurred";

      // Check if this is specifically a wallet error
      const isWalletError =
        detail?.isWalletError ||
        message.includes("could not coalesce error") ||
        message.includes("missing trie node") ||
        message.includes("Internal JSON-RPC error") ||
        message.includes("Failed to connect") ||
        message.includes("UNKNOWN_ERROR") ||
        message.includes("network error") ||
        message.includes("connection failed") ||
        message.includes("connector.disconnect");

      // Show error notification with appropriate message
      setNotify({
        status: "error",
        txHash: `undefined`,
        errorReason: isWalletError
          ? "Wallet connection error. Wallet disconnected. Please reconnect your wallet"
          : `${detail?.context || "Error"}: ${message}`,
        duration: isWalletError ? 10000 : 8000,
        chainType: "BNB",
      });
    };

    window.addEventListener("unhandledrejection", onUnhandledRejection);
    window.addEventListener("error", onGlobalError);
    window.addEventListener("app-error", onCustomError as EventListener);

    return () => {
      window.removeEventListener("unhandledrejection", onUnhandledRejection);
      window.removeEventListener("error", onGlobalError);
      window.removeEventListener("app-error", onCustomError as EventListener);
    };
  }, [setNotify]);

  return <>{children}</>;
};

export default GlobalErrorHandler;

// Utility function to manually report errors from anywhere in the app
export const reportError = (error: any, context?: string) => {
  window.dispatchEvent(
    new CustomEvent("app-error", {
      detail: {
        message: error?.message || error?.toString() || "Unknown error",
        context: context || "Manual Report",
        originalError: error,
      },
    })
  );
};
